import { addDays } from 'date-fns';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { eachDayOfInterval } from 'date-fns/eachDayOfInterval';
import { endOfWeek } from 'date-fns/endOfWeek';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isEqual } from 'date-fns/isEqual';
import { isMonday } from 'date-fns/isMonday';
import { isSunday } from 'date-fns/isSunday';
import { isWithinInterval } from 'date-fns/isWithinInterval';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfWeek } from 'date-fns/startOfWeek';
import * as React from 'react';
import { useStyles, noop } from '@sravni/react-utils';
import { Space } from '../../Space/src/Space.js';
import modules_75b426be from '../styles/Calendar.module.less.js';
import { Day } from './Day.js';

// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable complexity */
const Week = (props) => {
    const { date, month, disabledIntervals, minDate, maxDate, mode, className, selectionStart, selectionEnd, highlightedStart, highlightedEnd, onChange, onMouseEnter, ...restProps } = props;
    const cx = useStyles(modules_75b426be);
    const classNames = cx(className, 'week');
    const today = startOfDay(new Date());
    const start = startOfWeek(date, { weekStartsOn: 1 });
    const end = endOfWeek(date, { weekStartsOn: 1 });
    const isBetween = (date, min, max) => date &&
        min &&
        max &&
        (isAfter(startOfDay(date), startOfDay(min)) || isEqual(startOfDay(date), startOfDay(min))) &&
        (isBefore(startOfDay(date), startOfDay(max)) || isEqual(startOfDay(date), startOfDay(max)));
    const isDisabledDate = React.useCallback((date) => {
        let dateDisabled = false;
        if (!disabledIntervals)
            return false;
        for (let i = 0; i < disabledIntervals.length; i++) {
            const { from, to } = disabledIntervals[i];
            dateDisabled = isWithinInterval(startOfDay(date), {
                start: startOfDay(from),
                end: startOfDay(to),
            });
            if (dateDisabled) {
                return dateDisabled;
            }
        }
        return false;
    }, [disabledIntervals]);
    const isSelectableDate = React.useCallback((date) => {
        if (isDisabledDate(date)) {
            return false;
        }
        if (minDate && maxDate) {
            return isWithinInterval(date, { start: minDate, end: maxDate });
        }
        if (minDate && !maxDate) {
            return isAfter(date, minDate) || isEqual(date, minDate);
        }
        if (maxDate && !minDate) {
            return isBefore(date, maxDate) || isEqual(date, maxDate);
        }
        return true;
    }, [maxDate, minDate, isDisabledDate]);
    const isSelectedDate = React.useCallback((date) => {
        if (!selectionStart || !selectionEnd)
            return false;
        if (mode === 'range') {
            if (selectionStart && selectionEnd) {
                return isWithinInterval(startOfDay(date), {
                    start: startOfDay(selectionStart),
                    end: startOfDay(selectionEnd),
                });
            }
            return false;
        }
        return isEqual(date, selectionStart);
    }, [mode, selectionStart, selectionEnd]);
    const isHighlightedDate = React.useCallback((date) => highlightedStart &&
        highlightedEnd &&
        (isAfter(startOfDay(date), startOfDay(highlightedStart)) ||
            isEqual(startOfDay(date), startOfDay(highlightedStart))) &&
        (isBefore(startOfDay(date), startOfDay(highlightedEnd)) || isEqual(startOfDay(date), startOfDay(highlightedEnd))), [highlightedStart, highlightedEnd]);
    const renderDays = React.useMemo(() => eachDayOfInterval({ start, end }).map((day) => {
        const prevWeekDate = addDays(day, -7);
        const nextWeekDate = addDays(day, 7);
        const selectionPrevDaysCount = selectionStart && selectionEnd && isBetween(prevWeekDate, selectionStart, selectionEnd)
            ? differenceInCalendarDays(selectionEnd, selectionStart)
            : 0;
        const selectionNextDaysCount = selectionStart && selectionEnd && isBetween(nextWeekDate, selectionStart, selectionEnd)
            ? differenceInCalendarDays(selectionEnd, selectionStart)
            : 0;
        const highlightedPrevDaysCount = highlightedStart && highlightedEnd && isBetween(prevWeekDate, highlightedStart, highlightedEnd)
            ? differenceInCalendarDays(highlightedEnd, highlightedStart)
            : 0;
        const highlightedNextDaysCount = highlightedStart && highlightedEnd && isBetween(nextWeekDate, highlightedStart, highlightedEnd)
            ? differenceInCalendarDays(highlightedEnd, highlightedStart)
            : 0;
        const active = isSelectedDate(day);
        const selectable = isSelectableDate(day);
        const disabled = isDisabledDate(day) || !selectable;
        const isFirstDay = highlightedStart
            ? highlightedStart && isEqual(day, highlightedStart)
            : selectionStart && isEqual(day, selectionStart);
        const isLastDay = highlightedEnd
            ? highlightedEnd && isEqual(day, highlightedEnd)
            : selectionEnd && isEqual(day, selectionEnd);
        const isBetweenDay = (selectionStart && selectionEnd && isBetween(day, selectionStart, selectionEnd)) ||
            (highlightedStart && highlightedEnd && isBetween(day, highlightedStart, highlightedEnd));
        const monday = isMonday(day);
        const sunday = isSunday(day);
        const dayClassNames = cx({
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'without-top-left-border-radius': isBetweenDay && ((!isFirstDay && !monday) || selectionPrevDaysCount >= 7 || highlightedPrevDaysCount >= 7),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'without-bottom-left-border-radius': isBetweenDay && ((!isFirstDay && !monday) || selectionNextDaysCount >= 7 || highlightedNextDaysCount >= 7),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'without-top-right-border-radius': isBetweenDay && ((!isLastDay && !sunday) || selectionPrevDaysCount >= 7 || highlightedPrevDaysCount >= 7),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'without-bottom-right-border-radius': isBetweenDay && ((!isLastDay && !sunday) || selectionNextDaysCount >= 7 || highlightedNextDaysCount >= 7),
        });
        return (React.createElement(Day, { className: dayClassNames, key: day.getTime(), date: day, active: active, disabled: disabled, highlighted: isHighlightedDate(day), today: isEqual(day, today), onChange: onChange, onMouseEnter: !disabled ? onMouseEnter : noop }));
    }), [
        end,
        today,
        start,
        selectionEnd,
        selectionStart,
        highlightedStart,
        highlightedEnd,
        isDisabledDate,
        isHighlightedDate,
        isSelectableDate,
        isSelectedDate,
        onChange,
        onMouseEnter,
        cx,
    ]);
    return (React.createElement("div", null,
        React.createElement(Space, { size: 0, align: "center", justify: "center", className: classNames, ...restProps }, renderDays)));
};
Week.displayName = 'Week';

export { Week };
//# sourceMappingURL=Week.js.map
